export interface Sel {
  start: number
  end: number
}

export interface OpResult {
  value: string
  start: number
  end: number
}

const splice = (value: string, start: number, end: number, insert: string): string =>
  value.slice(0, start) + insert + value.slice(end)

export const wrapSelection = (
  value: string,
  sel: Sel,
  args: { prefix: string; suffix?: string; placeholder?: string },
): OpResult => {
  const suffix = args.suffix === undefined ? args.prefix : args.suffix
  const selected = value.slice(sel.start, sel.end)
  const placeholder = args.placeholder || ''
  const innerText = selected || placeholder
  const inserted = args.prefix + innerText + suffix
  const next = splice(value, sel.start, sel.end, inserted)
  if (selected) {
    return {
      value: next,
      start: sel.start + args.prefix.length,
      end: sel.start + args.prefix.length + selected.length,
    }
  }
  const caret = sel.start + args.prefix.length + placeholder.length
  return { value: next, start: caret, end: caret }
}

const lineBoundsAt = (value: string, pos: number): { lineStart: number; lineEnd: number } => {
  const lineStart = value.lastIndexOf('\n', pos - 1) + 1
  let lineEnd = value.indexOf('\n', pos)
  if (lineEnd === -1) lineEnd = value.length
  return { lineStart, lineEnd }
}

const expandToLineRange = (value: string, sel: Sel): { start: number; end: number } => {
  const { lineStart } = lineBoundsAt(value, sel.start)
  let end = sel.end
  if (end > sel.start && value[end - 1] === '\n') end -= 1
  const { lineEnd } = lineBoundsAt(value, end)
  return { start: lineStart, end: lineEnd }
}

export const toggleLinePrefix = (
  value: string,
  sel: Sel,
  args: { prefix: string },
): OpResult => {
  const { start, end } = expandToLineRange(value, sel)
  const block = value.slice(start, end)
  const lines = block.split('\n')
  const allHave = lines.every((l) => l.startsWith(args.prefix))
  const next = allHave
    ? lines.map((l) => l.slice(args.prefix.length)).join('\n')
    : lines.map((l) => args.prefix + l).join('\n')
  const newValue = splice(value, start, end, next)
  const delta = next.length - block.length
  return { value: newValue, start, end: end + delta }
}

export const toggleOrderedList = (value: string, sel: Sel): OpResult => {
  const { start, end } = expandToLineRange(value, sel)
  const block = value.slice(start, end)
  const lines = block.split('\n')
  const orderedRe = /^\d+\.\s/
  const allHave = lines.every((l) => orderedRe.test(l))
  const next = allHave
    ? lines.map((l) => l.replace(orderedRe, '')).join('\n')
    : lines.map((l, i) => `${i + 1}. ${l}`).join('\n')
  const newValue = splice(value, start, end, next)
  const delta = next.length - block.length
  return { value: newValue, start, end: end + delta }
}

export const cycleHeading = (value: string, sel: Sel): OpResult => {
  const { lineStart, lineEnd } = lineBoundsAt(value, sel.start)
  const line = value.slice(lineStart, lineEnd)
  const m = /^(#{1,6})\s/.exec(line)
  let nextLine: string
  if (!m) nextLine = '# ' + line
  else if (m[1].length === 1) nextLine = '## ' + line.slice(2)
  else if (m[1].length === 2) nextLine = '### ' + line.slice(3)
  else nextLine = line.slice(m[0].length)
  const newValue = splice(value, lineStart, lineEnd, nextLine)
  const delta = nextLine.length - line.length
  return { value: newValue, start: sel.start + delta, end: sel.end + delta }
}

export const insertLink = (
  value: string,
  sel: Sel,
  args: { url: string; label?: string },
): OpResult => {
  const selected = value.slice(sel.start, sel.end)
  const label = args.label || selected || 'link'
  const inserted = `[${label}](${args.url})`
  const next = splice(value, sel.start, sel.end, inserted)
  const caret = sel.start + inserted.length
  return { value: next, start: caret, end: caret }
}

export const wrapCodeBlock = (value: string, sel: Sel): OpResult => {
  const selected = value.slice(sel.start, sel.end)
  const needsLeadingNl = sel.start > 0 && value[sel.start - 1] !== '\n'
  const needsTrailingNl = sel.end < value.length && value[sel.end] !== '\n'
  const lead = needsLeadingNl ? '\n' : ''
  const trail = needsTrailingNl ? '\n' : ''
  const body = selected || 'code'
  const inserted = `${lead}\`\`\`\n${body}\n\`\`\`${trail}`
  const next = splice(value, sel.start, sel.end, inserted)
  if (selected) {
    const innerStart = sel.start + lead.length + 4
    return { value: next, start: innerStart, end: innerStart + body.length }
  }
  const caret = sel.start + lead.length + 4
  return { value: next, start: caret, end: caret + body.length }
}

export const insertAtCursor = (
  value: string,
  sel: Sel,
  args: { text: string },
): OpResult => {
  const inserted = args.text
  const next = splice(value, sel.start, sel.end, inserted)
  const caret = sel.start + inserted.length
  return { value: next, start: caret, end: caret }
}

export const safeHref = (href: string): string | null => {
  if (!href) return null
  const trimmed = String(href).trim()
  if (/^(https?:|mailto:|\/|#)/i.test(trimmed)) return trimmed
  if (/^[a-z0-9.\-_]+@[a-z0-9.\-_]+\.[a-z]{2,}$/i.test(trimmed)) return `mailto:${trimmed}`
  return null
}
